// COS-MODES (docs/design/2026-10-30-chief-of-staff-model.md §3, §6): the vocabulary of the routing
// verdict for an agent directing another person's agent. Kept apart from cos-modes.ts so the task
// routes (work.ts) can carry the verdict without an import cycle.
import { z } from "zod";
import { Timestamp, Uuid } from "../primitives";

export const CosMode = z.enum(["solo", "team"]).meta({ id: "CosMode" });
export type CosMode = z.infer<typeof CosMode>;
/** A head's level: Hands-off, Tell me (the Team default), Ask me first. */
export const DirectionLevel = z.enum(["hands_off", "inform", "ask"]).meta({ id: "DirectionLevel" });
export type DirectionLevel = z.infer<typeof DirectionLevel>;
/** What an agent does to another agent (design §6 action classes). */
export const DirectionAction = z
  .enum(["read_status", "assign", "priority", "urgent", "configure"])
  .meta({ id: "DirectionAction" });
export type DirectionAction = z.infer<typeof DirectionAction>;
/** The two action types a head may tick for Ask me first. */
export const AskableAction = z.enum(["assign", "priority"]).meta({ id: "AskableAction" });
export const DirectionFallback = z.enum(["proceed", "escalate"]).meta({ id: "DirectionFallback" });
export const DirectionOutcome = z
  .enum(["direct", "inform", "ask", "refused"])
  .meta({ id: "DirectionOutcome" });

/** A head's setting (`membership.settings.agent_direction`). */
export const AgentDirectionSetting = z
  .strictObject({
    level: DirectionLevel,
    ask_for: z.array(AskableAction).max(2),
    /** Working hours (the company's quiet hours do not count). */
    timeout_hours: z.int().min(1).max(24),
    fallback: DirectionFallback,
  })
  .meta({ id: "AgentDirectionSetting" });
export type AgentDirectionSetting = z.infer<typeof AgentDirectionSetting>;

export const DirectionPerson = z
  .object({ membership_id: Uuid, name: z.string() })
  .meta({ id: "DirectionPerson" });

/** The server's routing verdict (the tool result; the CoS never computes the chart itself). */
export const DirectionVerdict = z
  .object({
    outcome: DirectionOutcome,
    /** Nobody is told (a status read; a direct order in Solo). */
    silent: z.boolean(),
    reason: z.string(),
    head: DirectionPerson.nullable(),
    level: DirectionLevel.nullable(),
    timeout_hours: z.int().nullable(),
    fallback: DirectionFallback.nullable(),
    /** Ask: when the request proceeds or escalates (working hours). */
    expires_at: Timestamp.nullable(),
    approval_id: Uuid.nullable(),
  })
  .meta({ id: "DirectionVerdict" });
export type DirectionVerdict = z.infer<typeof DirectionVerdict>;
